import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {JSONObject, JSONValue} from 'util/Types';

import {
  FIELD_ORDER_UI_SCHEMA_KEY,
  isShapeUISchemaV2FieldComponentValue,
  isShapeUISchemaV2NestedShapeValues,
  SECTION_NAME_UI_SCHEMA_KEY,
  ShapeUISchemaV2,
  SHOW_DIVIDER_LINE_KEY,
} from '../StepConfigEditorTypes';
import StepConfigEditorRouterV2 from './StepConfigEditorRouterV2';
import StepConfigFieldsTemplate from './StepConfigFieldsTemplate';

interface ShapeEditorV2Props {
  config: JSONObject;
  configShape: PipelineConfigJsonSpec;
  onChangeConfig: (config: JSONValue, path: Array<string>, newValue: JSONValue) => void;
  disabled?: boolean;
  uiSchema: ShapeUISchemaV2;
}

/** @package */
export default function ShapeEditorV2({
  config,
  configShape,
  onChangeConfig,
  disabled,
  uiSchema,
}: ShapeEditorV2Props): JSX.Element {
  const fieldOrder = uiSchema[FIELD_ORDER_UI_SCHEMA_KEY];
  const sectionName = uiSchema[SECTION_NAME_UI_SCHEMA_KEY];

  if (fieldOrder.length > 0 && isShapeUISchemaV2NestedShapeValues(uiSchema)) {
    return (
      <>
        {fieldOrder.map((variableName) => (
          <StepConfigEditorRouterV2
            key={variableName}
            config={config}
            configShape={configShape}
            dataTestAttrs={{key: variableName}}
            onChangeConfig={onChangeConfig}
            disabled={disabled}
            uiSchema={uiSchema[variableName]}
          />
        ))}
      </>
    );
  }
  assert(
    isShapeUISchemaV2FieldComponentValue(uiSchema),
    'uiSchema must be a ShapeUISchemaV2FieldComponentValue'
  );
  const fields = fieldOrder.map((variableName) => {
    const field = uiSchema[variableName];
    assert(field != null, `Field ${variableName} is not defined in uiSchema`);
    return {
      variableName: field.displayName,
      infoText: field.infoText,
      editor: (
        <StepConfigEditorRouterV2
          key={variableName}
          config={config}
          configShape={configShape}
          dataTestAttrs={{key: variableName}}
          onChangeConfig={onChangeConfig}
          disabled={disabled}
          uiSchema={uiSchema[variableName]}
        />
      ),
    };
  });

  return (
    <>
      <StepConfigFieldsTemplate numColumns={1} fields={fields} sectionName={sectionName} isCompact={true} />
      {uiSchema[SHOW_DIVIDER_LINE_KEY] && <hr className={css(styles.dividerLine)} />}
    </>
  );
}

const styles = StyleSheet.create({
  dividerLine: {
    marginTop: 4,
    marginBottom: 10,
  },
});
